import { useQuery } from "@tanstack/react-query";
import { useApiService } from "@/hooks/useApiService";
import { getBranchWeeklySales } from "@/integrations/supabase/report-services";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Terminal } from "lucide-react";
import { BranchWeeklySales } from "@/types/models";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const WeeklySalesBranch = () => {
  const { callService } = useApiService();

  const { data: weeklySalesData, isLoading, isError, error } = useQuery<BranchWeeklySales[]>({
    queryKey: ['branchWeeklySales'],
    queryFn: () => callService(getBranchWeeklySales),
  });

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="flex items-center justify-between">
                <Skeleton className="h-5 w-1/3" />
                <Skeleton className="h-5 w-1/4" />
              </div>
              <Skeleton className="h-40 w-full rounded-md" />
            </div>
          ))}
        </div>
      );
    }

    if (isError) {
      return (
        <Alert variant="destructive">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            No se pudo cargar el gráfico de ventas semanales. Error: {error.message}
          </AlertDescription>
        </Alert>
      );
    }

    if (!weeklySalesData || weeklySalesData.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">
          No hay datos de ventas para la semana actual.
        </p>
      );
    }

    const chartData = weeklySalesData[0].sales.map(sale => {
      const dataPoint: { day: string; [key: string]: number | string } = { day: sale.day };
      weeklySalesData.forEach(branch => {
        const branchSale = branch.sales.find(s => s.day === sale.day);
        dataPoint[branch.branchName] = branchSale ? branchSale.total : 0;
      });
      return dataPoint;
    });

    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="day" />
          <YAxis />
          <Tooltip />
          <Legend />
          {weeklySalesData.map((branch, index) => (
            <Line
              key={branch.branchName}
              type="monotone"
              dataKey={branch.branchName}
              stroke={colors[index % colors.length]}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ventas de la Semana por Sucursal</CardTitle>
      </CardHeader>
      <CardContent>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default WeeklySalesBranch;
